
import React from 'react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Globe, Mail, ArrowRight } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { z } from 'zod';
import { SignupData } from '@/types/onboarding.d';

// Form schema for the referral setup step
export const referralSetupSchema = z.object({
  initialReferralUrl: z.string().url({ message: "Please enter a valid URL" }),
  supportEmail: z.string().email({ message: "Please enter a valid email address" }),
});

export type ReferralSetupFormValues = z.infer<typeof referralSetupSchema>;

interface ReferralSetupFormProps {
  form: UseFormReturn<ReferralSetupFormValues>;
  onSubmit: (data: ReferralSetupFormValues) => void; // Updated type signature to match parent component
  onBackStep: () => void;
  signupData: SignupData | null;
  isLoading: boolean;
}

const ReferralSetupForm: React.FC<ReferralSetupFormProps> = ({ 
  form, 
  onSubmit, 
  onBackStep, 
  signupData,
  isLoading
}) => {
  // Handle the form submission and pass the form values to the onSubmit callback
  const handleSubmit = (values: ReferralSetupFormValues) => {
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="initialReferralUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Initial Referral URL</FormLabel>
              <FormControl>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input 
                    className="pl-10" 
                    placeholder="https://www.yourcompany.com/landing" 
                    {...field} 
                  />
                </div>
              </FormControl>
              <p className="text-sm text-gray-500 mt-1">The page where referrals will be directed to</p>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="supportEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Support Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input 
                    className="pl-10" 
                    placeholder="support@yourcompany.com" 
                    type="email" 
                    {...field} 
                  />
                </div>
              </FormControl>
              <p className="text-sm text-gray-500 mt-1">For customer support inquiries</p>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Alert className="bg-blue-50 border-blue-200">
          <AlertTitle className="text-blue-800">Ready to complete setup</AlertTitle>
          <AlertDescription className="text-blue-700">
            We'll create your referral platform instance with these settings and send login credentials to {signupData?.email}
          </AlertDescription>
        </Alert>
        
        <div className="flex space-x-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onBackStep}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="arrow"
            className="flex-1"
            disabled={isLoading || !form.formState.isValid}
          >
            {isLoading ? "Setting Up..." : "Complete Setup"} {!isLoading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ReferralSetupForm;
